import type { StompSubscription } from '@stomp/stompjs';
import produce from 'immer';
import { ChannelTypeConstants } from 'packages/workspace/modules/algebra/constants';
import type { ITask } from 'packages/workspace/modules/algebra/interfaces/IOmniChannelPage';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { TaskRouterConfig } from 'packages/workspace/services/config';
import { phoneStore } from 'packages/workspace/services/store/omnichannel/phone-store';
import { BehaviorSubject } from 'rxjs';
import { SoundOptions } from '../useSoundStore';
import type { IReservation } from '../../../modules/algebra/interfaces/IOmniChannelPage';
import { QueueConfig } from '../../config';
import type { ChatConversationMessage } from '../../models/internal-channel';
import type { IOperator } from '../../models/operator';
import { chatStore, prepareChannelVirtualTopicForClient, prepareGroupChatVirtualTopicForClient } from './chat-store';
import type { IShiftSession } from './shift-store';
import merge from 'lodash.merge';
import type { Worker } from 'twilio-taskrouter';
import { createLogger } from '../../../modules/prelude/logger/logger';
import _ from 'lodash';

const fileLabel = 'taskrouter-store.ts';
const logger = createLogger({
  fileLabel,
});

export interface ITaskRouterState {
  requestWorkerSession: boolean;
  connectionStatus: string;
  workerClient: Record<string, unknown>;
  worker?: Worker;
  activities: Array<Record<string, unknown>>;
  channels: Array<Record<string, unknown>>;
  tasks: Array<ITask>;
  dummyTasks: Array<ITask>;
  error: boolean;
  errorMessage: string;
  reservations: Array<IReservation>;
  task_router_credentials: IShiftSession;
  activeTask: ITask | null;
  hasShiftInfo?: boolean;
  sessionId: string;
}

export const TaskRouterConnectionStates = {
  DISCONNECTED: 'disconnected',
  TOKENS_OBTAINED: 'tokens_obtained',
  READY_FOR_WORK: 'connected',
  CONNECTION_ERROR: 'error',
};

export const initialTaskRouterState: ITaskRouterState = {
  requestWorkerSession: false,
  connectionStatus: 'disconnected',
  workerClient: {},
  worker: undefined,
  activities: [],
  channels: [],
  tasks: [],
  dummyTasks: [],
  error: false,
  errorMessage: '',
  reservations: [],
  activeTask: null as unknown as ITask,
  task_router_credentials: {
    voice_token: '',
    task_router_token: '',
    appId: '',
    operator: null as unknown as IOperator,
    shift_started: 0,
    queues: [],
    profile: null,
    staffStatus: '',
    shift_id: '',
  },
  hasShiftInfo: false,
  sessionId: '',
};

const taskRouterSubject = new BehaviorSubject(initialTaskRouterState);

export const taskRouterStore = {
  setHasShiftInfo: (hasShiftInfo: boolean): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.hasShiftInfo = hasShiftInfo;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Used once token obtained
   */
  setCredentialsAndUpdateStatus: (tokens: IShiftSession): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.task_router_credentials = tokens;
      draftState.connectionStatus = draftState.connectionStatus === TaskRouterConnectionStates.READY_FOR_WORK ? TaskRouterConnectionStates.READY_FOR_WORK : TaskRouterConnectionStates.TOKENS_OBTAINED;
    });
    taskRouterSubject.next(state);
  },
  requestWorkerSession: (): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.requestWorkerSession = true;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Used when shift is ended.
   */
  endShift: (): void => {
    taskRouterSubject.value.worker?.removeAllListeners();
    taskRouterSubject.value.worker?.disconnect?.();
    chatStore?.disconnectClient();
    const state = produce(initialTaskRouterState, (draftState) => {
      draftState.hasShiftInfo = true;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Used to register Worker.
   * @param worker
   */
  registerWorker: (worker: any): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.connectionStatus = TaskRouterConnectionStates.READY_FOR_WORK;
      draftState.worker = worker;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Called when we create reservation.
   * @param reservation
   */
  reservationCreated: (reservation: IReservation): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.reservations.unshift(reservation);
      draftState.reservations[0].task = {
        active_message: '',
        attributes: reservation.attributes,
        rhs_task_id: JSON.parse(reservation.attributes).previous_task_sid
          ? JSON.parse(reservation.attributes).previous_task_sid.slice(QueueConfig.LAST_MINIMUM_CHARACTERS_TO_SHOW)
          : reservation.sid.slice(QueueConfig.LAST_MINIMUM_CHARACTERS_TO_SHOW),
      };
    });
    taskRouterSubject.next(state);
  },
  /**
   * called when we remove reservation.
   * @Param reservationSID
   */
  removeReservation: (reservationSId: string): void => {
    console.log("reservationSId::::::::", reservationSId)
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.reservations = draftState.reservations.filter((res) => JSON.parse(res.attributes).reservation_sid !== reservationSId);
    });
    console.log("state::::::::", state.reservations)
    taskRouterSubject.next(state);
  },
  removeReservationWithSid: (taskSid: string): void => {
    if (taskRouterSubject.value.reservations.length > 0) {
      const state = produce(taskRouterSubject.value, (draftState) => {
        draftState.reservations = draftState.reservations.filter((res) => res?.sid !== taskSid);
      });
      taskRouterSubject.next(state);
    }
  },
  /**
   * Called when we click on any task from task list.
   * @param task
   */
  setActiveTask: (task: ITask): void => {
    if (task?.sid !== taskRouterSubject.value.activeTask?.sid) {
      const state = produce(taskRouterSubject.value, (draftState) => {
        const taskObj = draftState.tasks.find((t) => t.sid === task.sid);
        if (taskObj) {
          taskObj.messages?.forEach((m) => (m.is_read = true));
          taskObj.task_group_chat?.forEach((m) => (m.is_read = true));
          draftState.activeTask = taskObj;
        }
        draftState.reservations = draftState.reservations.filter((eachReservation) => eachReservation.sid !== task.sid);
      });
      taskRouterSubject.next(state);
    }
  },
  /**
   * Null task set.
   */
  setNoActiveTask: (taskSid: string): void => {
    const task = taskRouterSubject.value.tasks.find((eachTask) => eachTask.sid === taskSid);
    task?.connection?.unsubscribe();
    task?.groupConnection?.unsubscribe();
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.tasks = draftState.tasks.filter((t) => t.sid !== taskSid);
      draftState.activeTask = draftState.tasks.length > 0 ? draftState.tasks[0] : null;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Called when we accept task.
   * @param task
   */
  taskAdded: (task: ITask): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      if (!draftState.tasks) {
        draftState.tasks = [];
        draftState.dummyTasks = [];
      }
      task.system_sid = task.attributes.previous_task_sid ? task.attributes.previous_task_sid : task.sid;
      task.channel_type = task.taskChannelUniqueName;
      task.rhs_task_id = task.system_sid.slice(QueueConfig.LAST_MINIMUM_CHARACTERS_TO_SHOW);
      draftState.tasks.push({ ...task });
      draftState.activeTask = { ...task };
      draftState.reservations = draftState.reservations.filter((res) => res.sid !== task.sid);
    });
    taskRouterSubject.next(state);
  },

  initialTaskStore: (task: ITask, flag = false): void => {
    if (!flag) {
      const state = produce(taskRouterSubject.value, (draftState) => {
        draftState.dummyTasks.push(task);
      });
      taskRouterSubject.next(state);
    } else {
      taskRouterStore.taskAdded(taskRouterSubject.value.dummyTasks[0]);
      const state = produce(taskRouterSubject.value, (draftState) => {
        draftState.dummyTasks = [];
      });
      taskRouterSubject.next(state);
    }
  },

  /**
   * Called when we set connection.
   * @param connection
   * @param taskSid
   * @param connection
   */
  setConnection: (taskSid: string, connection?: StompSubscription, groupConnection?: StompSubscription): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const taskObj = draftState.tasks.find((t) => t.sid === taskSid);
      if (taskObj) {
        taskObj.connection = connection;
        taskObj.groupConnection = groupConnection;
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * Used in case of refresh
   * @param task
   */
  updateTask: (task: ITask, swoopFlag = false): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((t) => t.sid === task.sid);
      const iTaskForSwoop = draftState.tasks.find((t) => t.sid === task.current_task_sid);
      const iTaskWithSystemSid = draftState.tasks.find((t) => t.system_sid === task.sid);
      if (iTaskWithSystemSid) {
        merge(iTaskWithSystemSid, { current_owner_account_name: task.current_owner_account_name });
      }
      if (iTask) {
        if (iTask.status === 'wrapping' && task.status === 'assigned') return;
        merge(iTask, task);
      }
      if (iTaskForSwoop && swoopFlag) {
        merge(iTaskForSwoop, { swoop_in: task.swoop_in, status: task.status, supervisor_sids: task.supervisor_sids });
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * When page is refreshed .
   */
  updateExistingTasks: (existingTasks: Array<ITask>): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      draftState.tasks = existingTasks.map((iExistingTask) => {
        const availableTask = draftState.tasks.find((task) => task.sid === iExistingTask.sid);
        if (availableTask) {
          return produce(iExistingTask, (draftTask) => {
            draftTask.messages = availableTask.messages;
            draftTask.task_group_chat = availableTask.task_group_chat;
          });
        }
        return iExistingTask;
      });
      const [activeTask] = draftState.tasks;
      draftState.activeTask = activeTask;
    });
    taskRouterSubject.next(state);
  },
  /**
   * Add task to supervision.
   */
  updateSupervisedTasks: (task: ITask, supervisor_id: string): void => {
    const isAlreadyInSupervision = taskRouterSubject.value.tasks.find((eachTask) => eachTask.sid === task.sid);
    if (!isAlreadyInSupervision) {
      const state = produce(taskRouterSubject.value, (draftState) => {
        const system_sid = task.previous_task_sid ? task.previous_task_sid : task.sid;
        const updatedTask = produce(task, (draftTask) => {
          draftTask.system_sid = system_sid;
          draftTask.rhs_task_id = system_sid.slice(QueueConfig.LAST_MINIMUM_CHARACTERS_TO_SHOW);
          draftTask.is_under_supervision = true;
          draftTask.attributes = {
            channel_type: task.channel_type,
            channel_id: task.channel,
            client: task.client,
          };
          if (
            [
              ChannelTypeConstants.TYPE_ONLINE_HOTLINE,
              ChannelTypeConstants.TYPE_ONLINE_TEXT,
              ChannelTypeConstants.TYPE_ONLINE_WHATSAPP,
              ChannelTypeConstants.TYPE_ONLINE_DISCORD,
              ChannelTypeConstants.TYPE_ONLINE_SIGNAL,
            ].includes(task.channel_type)
          ) {
            const topic_name = prepareChannelVirtualTopicForClient(
              draftState.sessionId,
              system_sid,
              task.channel_type
            );
            draftTask.connection = chatStore.subscribeToTopic(
              topic_name,
              (msg, event) => {
                taskRouterStore.updateSurvivorsConversation(msg, event);
              },
              {
                id: `task-${system_sid}`,
                ack: 'client',
              }
            );
          }
          draftTask.groupConnection = chatStore.subscribeToTopic(
            prepareGroupChatVirtualTopicForClient(draftState.sessionId, system_sid),
            (message) => {
              taskRouterStore.updateTaskRelatedGroupChat(message, system_sid, message.sender === supervisor_id);
            },
            {
              id: `task-internal-${system_sid}`,
              ack: 'client',
            }
          );
        });
        if (!draftState.tasks) {
          draftState.tasks = [];
        }
        draftState.tasks.push(updatedTask);
        if (task.channel_type === ChannelTypeConstants.TYPE_TELEPHONE_HOTLINE) {
          phoneStore.dialConference(system_sid).catch(logger.error);
        }
        if (!draftState.activeTask) {
          draftState.activeTask = updatedTask;
        }
      });
      taskRouterSubject.next(state);
    }
  },
  /**
   * Wrap Up Task
   * @param givenTaskSid
   */
  wrapUpTask: (givenTaskSid: string): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === givenTaskSid);
      if (iTask) {
        iTask.status = TaskRouterConfig.WRAPPING_STATUS;
      } else {
        console.log('task not found', taskRouterSubject.value.tasks);
      }
    });
    taskRouterSubject.next(state);
  },

  /**
   * Put task back in queue - Remove from tasks list
   * @param givenTaskSid
   */
  removeTask: (givenTaskSid: string): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      if (draftState.activeTask?.sid === givenTaskSid) {
        draftState.activeTask = null;
      }
      draftState.tasks = draftState.tasks.filter((task) => task.sid !== givenTaskSid);
    });
    taskRouterSubject.next(state);
  },
  /**
   * Used when we want to remove any supervised tasks.
   * @param task_sid
   */
  removeSupervision: (task_sid: string): void => {
    //check if active subs$ - unsubscribe.
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.connection?.unsubscribe();
        iTask.groupConnection?.unsubscribe();
        if (iTask.channel_type === ChannelTypeConstants.TYPE_TELEPHONE_HOTLINE) {
          phoneStore.disconnectActiveCall();
        }
      }
      draftState.tasks = draftState.tasks.filter((task) => task.sid !== task_sid);
      if (draftState.activeTask?.sid === task_sid) {
        draftState.activeTask = draftState.tasks.length > 0 ? draftState.tasks[0] : null;
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * This is Conversation with Survivors
   * @param message
   */
  updateSurvivorsConversation: (message: ChatConversationMessage, event): void => {
    if (event === 'WEBCHAT_END') {
      taskRouterStore.wrapUpTask(message.conversation_id);
    }
    if (!message.system) {
      SoundOptions.setType(`visitor_${message.id}`);
    }
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.system_sid === message.conversation_id);
      if (iTask) {
        if (!iTask.messages) {
          iTask.messages = [];
        }
        iTask.messages.push(
          produce(message, (draftMessage) => {
            draftMessage.is_read = draftState.activeTask?.system_sid === message.conversation_id;
          })
        );
      }
    });

    taskRouterSubject.next(state);
  },
  /**
   * 
   * @param groupChat
   */
  clearGroupMessagesOnTabChange: (groupChat = false): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks?.find((task) => task.system_sid === draftState.activeTask?.sid || task.system_sid === draftState.activeTask?.system_sid);
      if (iTask && !iTask.popped_out) {
        iTask.task_group_chat = [];
        if (!groupChat) {
          iTask.messages = [];
        }
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * This is Internal Group Conversation Per Task
   */
  updateTaskRelatedGroupChat: (message: ChatConversationMessage, task_sid: string, own_message: boolean): void => {

    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.system_sid === task_sid);
      if (iTask) {
        if (!iTask.task_group_chat) {
          iTask.task_group_chat = [];
        }
        iTask.task_group_chat.push(
          produce(message, (draftMessage) => {
            draftMessage.is_read = draftState.activeTask?.system_sid === message.conversation_id;
          })
        );
      }
    });
    if (!own_message) SoundOptions.setType(`internal_${task_sid}`);
    taskRouterSubject.next(state);
  },
  /**
   * Sending Resource to Task
   * @param activeMessage
   * @param task_sid
   */
  sendResourceToActiveChat: (activeMessage: string, task_sid: string) => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.system_sid === task_sid);
      if (iTask) {
        if (!iTask.is_under_supervision || iTask.swoop_in) {
          iTask.active_message = activeMessage;
        } else {
          iTask.active_group_message = activeMessage;
        }
      }
    });
    taskRouterSubject.next(state);
  },
  useResourceFromButton: (activeMessage: string) => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === draftState.activeTask?.sid);
      if (iTask) {
        iTask.active_message = activeMessage;
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * Pop Out a Task
   * @param taskSid
   * @param isPoppedOut
   */
  togglePopInOrOut: (taskSid: string, isPoppedOut: boolean): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === taskSid);
      if (iTask) {
        iTask.popped_out = isPoppedOut;
      }
      if (draftState.activeTask) {
        draftState.activeTask.popped_out = isPoppedOut;
      }
    });
    taskRouterSubject.next(state);
  },
  updateNotepadContent: (content: string, task_sid: string): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.notepad_content = content;
      }
    });
    taskRouterSubject.next(state);
  },
  clearResources: (task_sid: string): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.active_message = '';
      }
    });
    taskRouterSubject.next(state);
  },
  clearGroupResources: (task_sid: string): void => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.active_group_message = '';
      }
    });
    taskRouterSubject.next(state);
  },
  /**
   * 
   * @param task_sid 
   * @param isSwoopedIn 
   * @param operator_sid Swoopped in operator added when supervisor swoop in 
   */
  swoopInOut: (task_sid: string, isSwoopedIn: boolean, operator_sid = '') => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.active_message = '';
        iTask.swoop_in = isSwoopedIn;
        iTask.swoop_in_operator = isSwoopedIn ? operator_sid : '';
      }
    });
    taskRouterSubject.next(state);
  },
  transfer: (task_sid: string, isTransferred: boolean) => {
    const state = produce(taskRouterSubject.value, (draftState) => {
      const iTask = draftState.tasks.find((task) => task.sid === task_sid);
      if (iTask) {
        iTask.transferred = isTransferred;
      }
    });
    taskRouterSubject.next(state);
  },
  storeSessionId: (sessionId: string): void => {
    const state = produce(taskRouterSubject.value, (draffState) => {
      draffState.sessionId = sessionId;
    });
    taskRouterSubject.next(state);
  },
};

export default function useTaskRouter(): ITaskRouterState {
  return useObservable<ITaskRouterState>(taskRouterSubject, taskRouterSubject.value);
}
